
import React from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Client } from '@/models/Client';
import { useCanDeleteClient, useDeleteClient } from '@/hooks/clients';
import { useRolePermissions } from '@/hooks/useRolePermissions';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/use-mobile';

interface ClientDeleteDialogProps {
  client: Client | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ClientDeleteDialog: React.FC<ClientDeleteDialogProps> = ({
  client,
  open,
  onOpenChange,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { canManageClients } = useRolePermissions();
  const { data: deleteCheck, isLoading: isCheckingDelete } = useCanDeleteClient(
    client?.id || ''
  );
  const deleteClientMutation = useDeleteClient();

  const handleDelete = () => {
    if (client && canManageClients) {
      deleteClientMutation.mutate(client.id, {
        onSuccess: () => {
          onOpenChange(false);
        },
      });
    }
  };

  if (!client || !canManageClients) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent 
        side="bottom" 
        className="h-screen w-full rounded-none border-0 p-0 flex flex-col modal-safe-area-aware ios-modal-safe pwa-modal-header-fix"
      >
        <SheetHeader className="p-6 border-b flex-shrink-0 pt-[calc(1.5rem+env(safe-area-inset-top))] pwa-safe-header">
          <SheetTitle className="text-lg">
            {t('common.delete')} {client.firstName} {client.lastName}
          </SheetTitle>
          <SheetDescription className="text-sm text-muted-foreground">
            {isCheckingDelete ? (
              t('clients.checkingDelete')
            ) : deleteCheck?.canDelete ? (
              t('clients.deleteConfirmation')
            ) : (
              <>
                {t('clients.cannotDeleteClient')}{' '}
                <strong>
                  {client.firstName} {client.lastName}
                </strong>
                . {deleteCheck?.reason}
              </>
            )}
          </SheetDescription>
        </SheetHeader>
        
        <div className="flex-1 p-6 overflow-y-auto pb-safe-bottom">
          {!deleteCheck?.canDelete && deleteCheck && (
            <div className="space-y-3 text-sm text-muted-foreground">
              {deleteCheck.futureBookingsCount > 0 && (
                <div>• {t('clients.futureBookingsCount', { count: deleteCheck.futureBookingsCount })}</div>
              )}
              {deleteCheck.activeSubscriptionsCount > 0 && (
                <div>• {t('clients.activeSubscriptionsCount', { count: deleteCheck.activeSubscriptionsCount })}</div>
              )}
            </div>
          )}
          
          <div className="flex gap-3 mt-6">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              {t('common.cancel')}
            </Button>
            {deleteCheck?.canDelete && (
              <Button
                onClick={handleDelete}
                disabled={deleteClientMutation.isPending}
                variant="destructive"
                className="flex-1"
              >
                {deleteClientMutation.isPending
                  ? t('clients.deleting')
                  : t('common.delete') + ` ${client.firstName} ${client.lastName}`}
              </Button>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default ClientDeleteDialog;
